/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {FabConfirmationDialogComponent} from "@fabric/components/confirmation-dialog";
import {FabMessageService} from "@fabric/components/message";
import {DISMISS_MSG} from "../../../common/constants";
import {SheetSelectorComponent} from "../../../core/components/sheet-selector/sheet-selector.component";
import {SheetSelectorInput} from "../../../core/models/sheet-selector-input.model";
import {StateService} from "../../../core/state/state.service";
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from "../../../services/excel-export.service";
import {VehicleScenarioService} from "../../../services/vehicle-scenario.service";

@Component({
    selector: 'app-vehicle-forecast-template-save',
    templateUrl: './vehicle-forecast-template-save.component.html',
    styleUrl: './vehicle-forecast-template-save.component.scss',
    standalone: false
})
export class VehicleForecastTemplateSaveComponent {

  // By Template
  file: File;
  sheets: string[];
  selectedSheet: string;

  showSpinner: boolean = false;

  constructor(public snackBar: FabMessageService,
              public stateService: StateService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              public readonly excelService: ExcelExportService,
              public readonly dialog: MatDialog,
              readonly router: Router) {
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.selectedSheet = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.selectedSheet.length === 0) {
          this.file = null;
        }
        this.getScenarioFromForecastTemplate();
      }
    );
  }

  /**
   * Get the scenario from the forecast template
   */
  getScenarioFromForecastTemplate(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getScenarioFromForecastTemplate(this.file, this.selectedSheet, this.stateService.getUserGmid()).subscribe({
      next: (data) => {
        if (data) {
          this.openConfirmationDialog(data);
        }
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
      }
    })
  }

  /**
   * Saves the Vehicle Forecast Template
   */
  saveVehicleForecastTemplate(vehScen: VehicleScenario): void {
    this.showSpinner = true;
    this.vehicleScenarioService.saveVehicleForecastTemplate(this.file, this.selectedSheet, this.stateService.getUserGmid()).subscribe({
        error: (error) => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Vehicle Forecast Template saved successfully', DISMISS_MSG);
          this.showSuccessfulSaveDialog(vehScen);
        }
      }
    );
  }

  /**
   * Open confirmation dialog to confirm saving the vehicle forecast template
   * @param vehScenId the id of the vehicle scenario to save to
   * @param vehScenName the name of the vehicle scenario to save to
   */
  openConfirmationDialog(vehScen: VehicleScenario): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      data: {
        title: 'Save Vehicle Forecast Template',
        message: 'Save data back to scenario ' + vehScen.vehScenId + ': "' + vehScen.vehScenName + '"?',
        noLabel: 'Cancel',
        yesLabel: 'Confirm',
        actionColor: 'primary'
      }
    });

    // If user confirms, proceed with the template save
    dialogRef.componentInstance.yes.subscribe(() => {
      this.saveVehicleForecastTemplate(vehScen);
    });
  }

  /**
   * Shows a confirmation dialog after a successful save that allows the user to select the updated scenario
   */
  showSuccessfulSaveDialog(vehScen: VehicleScenario): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Scenario Successfully Updated',
        message: 'Would you like to select scenario ' + vehScen.vehScenId + ': "' + vehScen.vehScenName + '"?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });
    dialogRef.componentInstance.yes.subscribe(() => {
      this.stateService.setSelectedScenario(vehScen, true);
      this.stateService.setCompareScenario(vehScen);
      this.router.navigate(['/']);
    });
    dialogRef.componentInstance.no.subscribe(() => {
      dialogRef.close();
    });
  }
}
